import { useState, useEffect } from 'react'
import { getDataAPI, postDataAPI, putDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'
import { useLocation } from 'react-router-dom'

interface Show {
  id: string
  date?: string
  perf_date?: string
  title?: string
  event_title?: string
  venue?: string
  city?: string
  ticket_count?: number
  current_ticket_count?: number
  tix_sold?: number
  status?: string
  sellable_cap?: number
  tix_sold_percent?: number
  artist?: string
  raw?: any
}

type FieldType = 'textarea' | 'checkbox' | 'select' | 'date' | 'number'

type Field = {
  key: string
  label: string
  type?: FieldType
  required?: boolean
  options?: string[]
  placeholder?: string
}

export default function ShowsPage() {
  // Navigate not used yet - kept for future use
  // const _navigate = useNavigate()
  const location = useLocation()
  const showsCacheKey = 'showsPageCache'
  const [shows, setShows] = useState<Show[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [addView, setAddView] = useState<'options' | 'manual' | 'csv' | 'bandsintown' | 'ticket-counter'>(
    'options'
  )
  // Mirrors legacy Bookings/ManualAdd.jsx fields
  const [editData, setEditData] = useState<Record<string, any>>({
    artist: '',
    perf_date: new Date().toISOString().split('T')[0],
    city: '',
    state: '',
    venue: '',
    event_title: '',
    status: 'pending',
    show_time: '',
    announce_date: '',
    on_sale_date: '',
    guarantee: '',
    walkout_potential: '',
    sellable_cap: '',
    promoter: '',
    tix_sold_percent: '',
    tix_sold: '',
    current_ticket_count: '',
    // advanced
    appearing_with: '',
    red_flag: false,
    red_flag_notes: '',
    promoter_company: '',
    promoter_contact_names: '',
    promoter_contact_emails: '',
    venue_category: '',
    billing_type: '',
    territory: '',
    advanced_info: '',
    deal_type: '',
    deal_info: '',
    gross_potential: '',
    net_potential: '',
    artist_net: '',
    artist_profit: '',
    split_point: '',
    amount_to_split: '',
    ticket_notes: '',
    ticket_prices_tiers: '',
    ticket_taxes_fees: '',
    total_fees: '',
    deposit_due: '',
    deposit_received: '',
    contract_status: '',
    currency_conversion: '',
    additional_clauses: '',
    schedule: '',
    notes: '',
  })
  const [csvFileName, setCsvFileName] = useState<string | null>(null)
  const [csvRows, setCsvRows] = useState<Record<string, any>[]>([])
  const [csvLoading, setCsvLoading] = useState(false)
  const [csvImporting, setCsvImporting] = useState(false)
  const [csvError, setCsvError] = useState<string | null>(null)
  const [bandsintownArtist, setBandsintownArtist] = useState('')
  const [bandsintownAppId, setBandsintownAppId] = useState('')
  const [bandsintownEvents, setBandsintownEvents] = useState<Record<string, any>[]>([])
  const [bandsintownLoading, setBandsintownLoading] = useState(false)
  const [bandsintownImporting, setBandsintownImporting] = useState(false)
  const [bandsintownError, setBandsintownError] = useState<string | null>(null)
  const [ticketCounts, setTicketCounts] = useState<Record<string, string>>({})
  const [ticketPercentages, setTicketPercentages] = useState<Record<string, string>>({})
  const [ticketSaving, setTicketSaving] = useState<Record<string, boolean>>({})
  const [ticketDirty, setTicketDirty] = useState<Set<string>>(new Set())
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editShowId, setEditShowId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedShowIds, setSelectedShowIds] = useState<Set<string>>(new Set())
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  const basicFields: Field[] = [
    { key: 'artist', label: 'Artist', required: true },
    { key: 'perf_date', label: 'Performance Date', type: 'date', required: true },
    { key: 'city', label: 'City', required: true },
    { key: 'state', label: 'State/Province', required: true },
    { key: 'venue', label: 'Venue', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['pending', 'confirmed', 'announced', 'private', 'canceled'] },
    { key: 'show_time', label: 'Set Time', placeholder: 'e.g., 9:00 PM' },
    { key: 'announce_date', label: 'Announce Date', type: 'date' },
    { key: 'on_sale_date', label: 'On Sale Date', type: 'date' },
    { key: 'event_title', label: 'Event Title' },
    { key: 'guarantee', label: 'Guarantee', placeholder: 'Enter amount' },
    { key: 'walkout_potential', label: 'Walkout', placeholder: 'Enter amount' },
    { key: 'sellable_cap', label: 'Sellable Cap', type: 'number', placeholder: 'Enter capacity' },
    { key: 'promoter', label: 'Promoter' },
    { key: 'tix_sold_percent', label: '% Tix Sold', type: 'number' },
    { key: 'tix_sold', label: 'Tix Sold', type: 'number' },
    { key: 'current_ticket_count', label: 'Current Ticket Count', type: 'number' },
  ] as const

  const advancedFields: Field[] = [
    { key: 'appearing_with', label: 'Lineup' },
    { key: 'red_flag', label: 'Red Flag', type: 'checkbox' },
    { key: 'red_flag_notes', label: 'Red Flag Notes', type: 'textarea' },
    { key: 'promoter_company', label: 'Promoter Company' },
    { key: 'promoter_contact_names', label: 'Promoter Contact Name(s)' },
    { key: 'promoter_contact_emails', label: 'Promoter Contact Email(s)' },
    { key: 'venue_category', label: 'Venue Category' },
    { key: 'billing_type', label: 'Billing Type' },
    { key: 'territory', label: 'Territory' },
    { key: 'advanced_info', label: 'Advanced Info', type: 'textarea' },
    { key: 'deal_type', label: 'Deal Type' },
    { key: 'deal_info', label: 'Deal Info' },
    { key: 'gross_potential', label: 'Gross Potential' },
    { key: 'net_potential', label: 'Net Potential' },
    { key: 'artist_net', label: 'Artist Net' },
    { key: 'artist_profit', label: 'Artist Profit' },
    { key: 'split_point', label: 'Split Point' },
    { key: 'amount_to_split', label: 'Amount to Split' },
    { key: 'ticket_notes', label: 'Ticket Notes', type: 'textarea' },
    { key: 'ticket_prices_tiers', label: 'Ticket Prices (tiers)' },
    { key: 'ticket_taxes_fees', label: 'Ticket Taxes / Fees' },
    { key: 'total_fees', label: 'Total Fees' },
    { key: 'deposit_due', label: 'Deposit Due' },
    { key: 'deposit_received', label: 'Deposit Received' },
    { key: 'contract_status', label: 'Contract Status' },
    { key: 'currency_conversion', label: 'Currency / Conversion' },
    { key: 'additional_clauses', label: 'Additional Clauses', type: 'textarea' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ] as const

  const renderField = (field: Field) => {
    const value = editData[field.key] ?? ''
    const common = 'ds-input'

    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
          placeholder={field.placeholder}
          className={`${common} min-h-[90px]`}
        />
      )
    }

    if (field.type === 'checkbox') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.checked }))}
            className="h-4 w-4"
          />
          <span className="text-sm text-gray-600">Yes</span>
        </div>
      )
    }

    if (field.type === 'select') {
      return (
        <select
          value={value}
          onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
          className={common}
        >
          {(field.options || []).map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )
    }

    if (field.type === 'date') {
      return (
        <DatePicker
          value={value}
          onChange={(newValue) => setEditData((prev) => ({ ...prev, [field.key]: newValue }))}
          placeholder="yyyy-mm-dd"
          required={!!field.required}
        />
      )
    }

    const inputType = field.type === 'number' ? 'number' : 'text'
    return (
      <input
        type={inputType}
        value={value}
        required={!!field.required}
        onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
        placeholder={field.placeholder}
        className={common}
      />
    )
  }

  const normalizeBookings = (payload: any) => {
    const list = payload?.data?.data || payload?.data || payload || []
    const array = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : []
    return array.map((item: any) => ({
      id: item._id || item.id,
      date: item.date || item.perf_date,
      perf_date: item.perf_date || item.date,
      title: item.event_title || item.title || item.venue,
      event_title: item.event_title || item.title,
      venue: item.venue,
      city: item.city,
      artist: item.artist || item.artist_name,
      ticket_count: item.ticket_count || item.tickets_sold || item.tix_sold || 0,
      current_ticket_count: item.current_ticket_count,
      tix_sold: item.tix_sold || item.tickets_sold,
      status: item.status,
      sellable_cap: item.sellable_cap || item.capacity,
      tix_sold_percent: item.tix_sold_percent || item.percentage_sold,
      raw: item,
    }))
  }

  const resetAddModal = () => {
    setAddView('options')
    setShowAdvanced(false)
    setCsvFileName(null)
    setCsvRows([])
    setCsvLoading(false)
    setCsvImporting(false)
    setCsvError(null)
    setBandsintownArtist('')
    setBandsintownAppId('')
    setBandsintownEvents([])
    setIsEditing(false)
    setEditShowId(null)
    setOpenDropdown(null)
    setBandsintownLoading(false)
    setBandsintownImporting(false)
    setBandsintownError(null)
  }

  const getTicketCount = (show: Show) =>
    Number(show.current_ticket_count || show.ticket_count || show.tix_sold || 0)

  const readCachedShows = () => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.localStorage.getItem(showsCacheKey)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.shows || !Array.isArray(parsed.shows)) return null
      return parsed.shows as Show[]
    } catch (error) {
      console.warn('Failed to read shows cache:', error)
      return null
    }
  }

  const writeCachedShows = (cachedShows: Show[]) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        showsCacheKey,
        JSON.stringify({ timestamp: Date.now(), shows: cachedShows })
      )
    } catch (error) {
      console.warn('Failed to write shows cache:', error)
    }
  }

  useEffect(() => {
    const cached = readCachedShows()
    if (cached && cached.length) {
      setShows(cached)
      setIsLoading(false)
      return
    }

    const fetchShows = async () => {
      try {
        const data = await getDataAPI('user/getBookings')
        setShows(normalizeBookings(data))
      } catch (error) {
        console.error('Error fetching shows:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchShows()
  }, [])

  useEffect(() => {
    if (isLoading) return
    if (!shows.length) return
    writeCachedShows(shows)
  }, [shows, isLoading])

  useEffect(() => {
    const counts: Record<string, string> = {}
    const percentages: Record<string, string> = {}
    shows.forEach((show) => {
      const id = show.id
      if (!id) return
      counts[id] = String(getTicketCount(show) || '')
      if (show.tix_sold_percent !== undefined && show.tix_sold_percent !== null) {
        percentages[id] = String(show.tix_sold_percent)
      }
    })
    setTicketCounts(counts)
    setTicketPercentages(percentages)
    setTicketDirty(new Set())
  }, [shows])

  // Check for edit item from navigation state
  useEffect(() => {
    const state = location.state as any
    if (state?.editItemId && state?.source === 'show' && shows.length > 0) {
      const show = shows.find((s) => s.id === state.editItemId)
      if (show) {
        startEditShow(show)
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, shows])

  const refresh = async () => {
    const data = await getDataAPI('user/getBookings').catch(() => [])
    setShows(normalizeBookings(data))
  }

  const parseCsvText = (text: string) => {
    const rows: string[][] = []
    let current = ''
    let row: string[] = []
    let inQuotes = false

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i]
      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          current += '"'
          i += 1
        } else {
          inQuotes = !inQuotes
        }
        continue
      }

      if (char === ',' && !inQuotes) {
        row.push(current)
        current = ''
        continue
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && text[i + 1] === '\n') {
          i += 1
        }
        row.push(current)
        if (row.some((value) => value.trim() !== '')) {
          rows.push(row)
        }
        row = []
        current = ''
        continue
      }

      current += char
    }

    if (current.length || row.length) {
      row.push(current)
      if (row.some((value) => value.trim() !== '')) {
        rows.push(row)
      }
    }

    return rows
  }

  const handleCsvFileChange = async (file: File) => {
    setCsvError(null)
    setCsvLoading(true)
    try {
      const fileName = file.name.toLowerCase()
      if (!fileName.endsWith('.csv')) {
        setCsvError('Excel files are not supported yet. Please export as CSV and re-upload.')
        setCsvRows([])
        setCsvFileName(file.name)
        return
      }

      const text = await file.text()
      const rows = parseCsvText(text)
      if (!rows.length) {
        setCsvError('No rows found in CSV.')
        setCsvRows([])
        return
      }

      const headers = rows[0].map((value) => value.trim())
      const normalizeHeader = (header: string) =>
        header
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
      // Legacy city → state/province auto-fill map (ported from `legacy/src/utils/csvParsers.js`)
      const cityStateData: Record<string, string> = {
        // United States
        'New York': 'NY',
        'Los Angeles': 'CA',
        Chicago: 'IL',
        Houston: 'TX',
        Phoenix: 'AZ',
        Philadelphia: 'PA',
        'San Antonio': 'TX',
        'San Diego': 'CA',
        Dallas: 'TX',
        'San Jose': 'CA',
        Austin: 'TX',
        Jacksonville: 'FL',
        'Fort Worth': 'TX',
        Columbus: 'OH',
        Charlotte: 'NC',
        'San Francisco': 'CA',
        Indianapolis: 'IN',
        Seattle: 'WA',
        Denver: 'CO',
        Washington: 'DC',
        Boston: 'MA',
        'El Paso': 'TX',
        Nashville: 'TN',
        Detroit: 'MI',
        'Oklahoma City': 'OK',
        Portland: 'OR',
        'Las Vegas': 'NV',
        Memphis: 'TN',
        Louisville: 'KY',
        Baltimore: 'MD',
        Milwaukee: 'WI',
        Albuquerque: 'NM',
        Tucson: 'AZ',
        Fresno: 'CA',
        Sacramento: 'CA',
        Mesa: 'AZ',
        'Kansas City': 'MO',
        Atlanta: 'GA',
        'Long Beach': 'CA',
        'Colorado Springs': 'CO',
        Raleigh: 'NC',
        Miami: 'FL',
        'Virginia Beach': 'VA',
        Omaha: 'NE',
        Oakland: 'CA',
        Minneapolis: 'MN',
        Tulsa: 'OK',
        Arlington: 'TX',
        Tampa: 'FL',
        'New Orleans': 'LA',

        // Canada
        Toronto: 'ON',
        Montreal: 'QC',
        Vancouver: 'BC',
        Calgary: 'AB',
        Edmonton: 'AB',
        Ottawa: 'ON',
        Winnipeg: 'MB',
        'Quebec City': 'QC',
        Hamilton: 'ON',
        Kitchener: 'ON',
        'London (ON)': 'ON',
        Victoria: 'BC',
        Halifax: 'NS',
        Oshawa: 'ON',
        Windsor: 'ON',

        // United Kingdom
        London: 'England',
        Birmingham: 'England',
        Manchester: 'England',
        Glasgow: 'Scotland',
        Liverpool: 'England',
        Edinburgh: 'Scotland',
        Leeds: 'England',
        Sheffield: 'England',
        Bristol: 'England',
        Cardiff: 'Wales',
        Belfast: 'Northern Ireland',
        'Newcastle (UK)': 'England',
        Nottingham: 'England',
        Portsmouth: 'England',
        Plymouth: 'England',

        // Australia
        Sydney: 'NSW',
        Melbourne: 'VIC',
        Brisbane: 'QLD',
        Perth: 'WA',
        Adelaide: 'SA',
        'Gold Coast': 'QLD',
        'Newcastle (AU)': 'NSW',
        Canberra: 'ACT',
        'Sunshine Coast': 'QLD',
        Wollongong: 'NSW',
        Hobart: 'TAS',
        Geelong: 'VIC',
        Townsville: 'QLD',

        // Germany
        Berlin: 'Berlin',
        Hamburg: 'Hamburg',
        Munich: 'Bavaria',
        Cologne: 'North Rhine-Westphalia',
        Frankfurt: 'Hesse',
        Stuttgart: 'Baden-Württemberg',
        Düsseldorf: 'North Rhine-Westphalia',
        Dortmund: 'North Rhine-Westphalia',
        Essen: 'North Rhine-Westphalia',
        Leipzig: 'Saxony',
        Bremen: 'Bremen',
        Dresden: 'Saxony',
        Hanover: 'Lower Saxony',
      }

      const titleCaseCity = (input: string) =>
        input
          .trim()
          .split(/\s+/)
          .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
          .join(' ')

      const lookupStateFromCity = (cityRaw: string): string | undefined => {
        const city = cityRaw.trim()
        if (!city) return undefined
        if (cityStateData[city]) return cityStateData[city]
        const tc = titleCaseCity(city)
        if (cityStateData[tc]) return cityStateData[tc]
        return undefined
      }

      // Legacy-style date normalization (e.g. "10.04.2025" → "2025-10-04")
      const normalizeDate = (raw: string): string | undefined => {
        const value = raw.trim()
        if (!value) return undefined

        const patterns: Array<{ re: RegExp; kind: 'mdy' | 'dmy' | 'ymd' }> = [
          { re: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, kind: 'mdy' }, // MM.DD.YYYY
          { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, kind: 'mdy' }, // MM/DD/YYYY
          { re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, kind: 'dmy' }, // DD-MM-YYYY
          { re: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, kind: 'ymd' }, // YYYY-MM-DD
        ]

        for (const p of patterns) {
          const m = value.match(p.re)
          if (!m) continue

          let year = ''
          let month = ''
          let day = ''

          if (p.kind === 'ymd') {
            year = m[1]
            month = m[2]
            day = m[3]
          } else if (p.kind === 'mdy') {
            month = m[1]
            day = m[2]
            year = m[3]
          } else {
            day = m[1]
            month = m[2]
            year = m[3]
          }

          const mm = String(Number(month)).padStart(2, '0')
          const dd = String(Number(day)).padStart(2, '0')
          const yy = String(Number(year))

          const d = new Date(Number(yy), Number(mm) - 1, Number(dd))
          if (
            d.getFullYear() === Number(yy) &&
            d.getMonth() === Number(mm) - 1 &&
            d.getDate() === Number(dd)
          ) {
            return `${yy}-${mm}-${dd}`
          }
        }

        // Fallback: try Date parsing
        const parsed = new Date(value)
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toISOString().split('T')[0]
        }

        return undefined
      }

      const normalizeStatus = (raw: string): string => {
        const v = raw.trim().toLowerCase()
        if (!v) return 'pending'
        if (v.includes('confirm') || v.includes('published')) return 'confirmed'
        if (v.includes('pending') || v.includes('draft')) return 'pending'
        if (v.includes('announce')) return 'announced'
        if (v.includes('private')) return 'private'
        if (v.includes('cancel')) return 'canceled'
        // PXS / agent exports sometimes have codes like DNA; treat as pending by default
        return 'pending'
      }
      const splitCityState = (raw: string): { city?: string; state?: string } => {
        const value = raw.trim()
        if (!value) return {}

        // Common delimiters from legacy + additional slash support
        const trySplit = (delimiter: string) => {
          const parts = value.split(delimiter).map((p) => p.trim()).filter(Boolean)
          if (parts.length >= 2) return { city: parts[0], state: parts[1] }
          return null
        }

        const comma = trySplit(',')
        if (comma) return comma
        const dash = value.includes(' - ') ? trySplit(' - ') : null
        if (dash) return dash
        const slash = value.includes('/') ? trySplit('/') : null
        if (slash) return slash

        // Handle "City ST" (e.g. "Austin TX")
        const m = value.match(/^(.*\S)\s+([A-Za-z]{2,3})$/)
        if (m) {
          const city = m[1].trim()
          const state = m[2].toUpperCase()
          return { city, state }
        }

        // If we can't confidently split, treat as city-only
        return { city: value }
      }

      const postProcessCityState = (data: Array<Record<string, any>>) => {
        return data.map((row) => {
          const next = { ...row }
          const cityStateRaw = typeof next.city_state === 'string' ? next.city_state : undefined
          if (cityStateRaw) {
            const hasCity = typeof next.city === 'string' && next.city.trim().length > 0
            const hasState = typeof next.state === 'string' && next.state.trim().length > 0
            if (!hasCity || !hasState) {
              const parsed = splitCityState(cityStateRaw)
              if (!hasCity && parsed.city) next.city = parsed.city
              if (!hasState && parsed.state) next.state = parsed.state
              if (
                (!hasState && (typeof next.state !== 'string' || !next.state.trim())) &&
                typeof next.city === 'string' &&
                next.city.trim()
              ) {
                const inferred = lookupStateFromCity(next.city)
                if (inferred) next.state = inferred
              }
            }
            // Match legacy: remove intermediate field so backend doesn't see unknown key
            delete next.city_state
          }

          // Legacy behavior: if city exists but state doesn't, try to infer
          if (
            typeof next.city === 'string' &&
            next.city.trim() &&
            (typeof next.state !== 'string' || !next.state.trim())
          ) {
            const inferred = lookupStateFromCity(next.city)
            if (inferred) next.state = inferred
          }

          return next
        })
      }
      const headerMap: Record<string, string> = {
        'perf date': 'perf_date',
        'performance date': 'perf_date',
        'show date': 'perf_date',
        date: 'perf_date',
        'event date': 'perf_date',
        artist: 'artist',
        'city state': 'city_state',
        location: 'city_state',
        city: 'city',
        state: 'state',
        'state province': 'state',
        venue: 'venue',
        'event title': 'event_title',
        status: 'status',
        'set time': 'show_time',
        'show time': 'show_time',
        'announce date': 'announce_date',
        'on sale date': 'on_sale_date',
        guarantee: 'guarantee',
        walkout: 'walkout_potential',
        'walkout potential': 'walkout_potential',
        'sellable cap': 'sellable_cap',
        capacity: 'sellable_cap',
        promoter: 'promoter',
        '% tix sold': 'tix_sold_percent',
        'tix sold': 'tix_sold',
        'current ticket count': 'current_ticket_count',
        'ticket link': 'ticket_link',
        notes: 'notes',
      }

      const mappedRows = rows.slice(1).map((row) => {
        const entry: Record<string, any> = {}
        headers.forEach((header, index) => {
          const key = headerMap[normalizeHeader(header)]
          if (!key) return
          const value = row[index]?.trim()
          if (value !== undefined && value !== '') {
            if (key === 'status') {
              entry[key] = normalizeStatus(value)
              return
            }
            if (key.endsWith('_date') || key === 'perf_date') {
              entry[key] = normalizeDate(value) || value
              return
            }
            entry[key] = value
          }
        })
        return entry
      })

      const cleanedRows = postProcessCityState(
        mappedRows.filter((row) => row.perf_date || row.venue || row.city || row.artist)
      )

      const finalRows = cleanedRows.filter(
        (row) => row.perf_date || row.venue || row.city || row.artist
      )

      if (!finalRows.length) {
        setCsvError('CSV parsed, but no valid rows matched known fields.')
        setCsvRows([])
        return
      }

      setCsvFileName(file.name)
      setCsvRows(finalRows)
    } catch (error) {
      console.error('CSV parse failed:', error)
      setCsvError('Failed to parse CSV file.')
      setCsvRows([])
    } finally {
      setCsvLoading(false)
    }
  }

  const handleCsvImport = async () => {
    if (!csvRows.length) return
    setCsvImporting(true)
    try {
      await postDataAPI('user/saveBookings', { data: csvRows })
      setIsAddOpen(false)
      resetAddModal()
      await refresh()
    } catch (error) {
      console.error('CSV import failed:', error)
      const maybeAxios = error as { response?: { data?: any; status?: number } }
      const msg =
        (typeof maybeAxios?.response?.data?.message === 'string' && maybeAxios.response.data.message) ||
        (typeof maybeAxios?.response?.data === 'string' && maybeAxios.response.data) ||
        (error instanceof Error ? error.message : null) ||
        'CSV import failed. Please verify the file and try again.'
      setCsvError(msg)
    } finally {
      setCsvImporting(false)
    }
  }

  const handleBandsintownPreview = async () => {
    if (!bandsintownArtist.trim() || !bandsintownAppId.trim()) return
    setBandsintownError(null)
    setBandsintownLoading(true)
    try {
      const response = await fetch(
        `https://rest.bandsintown.com/artists/${encodeURIComponent(
          bandsintownArtist.trim()
        )}/events?app_id=${encodeURIComponent(bandsintownAppId.trim())}`
      )
      if (!response.ok) {
        throw new Error('Failed to fetch events')
      }
      const events = await response.json()
      if (!Array.isArray(events) || events.length === 0) {
        setBandsintownError('No upcoming events found for this artist.')
        setBandsintownEvents([])
        return
      }

      const bookings = events.map((event: any) => {
        const perfDate = new Date(event.datetime)
        return {
          perf_date: perfDate.toISOString().split('T')[0],
          artist: bandsintownArtist.trim(),
          venue: event.venue?.name,
          city: event.venue?.city,
          state: event.venue?.region || event.venue?.country,
          country: event.venue?.country,
          status: 'confirmed',
          ticket_link: event.url,
          promoter: event.venue?.name,
          bandsintown_id: event.id,
          source: 'bandsintown',
          venue_address: `${event.venue?.city || ''}, ${event.venue?.region || event.venue?.country || ''}`.trim(),
          show_time: perfDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
          event_description: event.description || '',
          appearing_with: Array.isArray(event.lineup) ? event.lineup.join(', ') : bandsintownArtist.trim(),
          event_title: event.title || event.venue?.name,
        }
      })

      setBandsintownEvents(bookings)
    } catch (error) {
      console.error('Bandsintown import failed:', error)
      setBandsintownError('Failed to fetch Bandsintown events. Please verify credentials.')
      setBandsintownEvents([])
    } finally {
      setBandsintownLoading(false)
    }
  }

  const handleBandsintownImport = async () => {
    if (!bandsintownEvents.length) return
    setBandsintownImporting(true)
    try {
      await postDataAPI('user/saveBookings', { data: bandsintownEvents })
      setIsAddOpen(false)
      resetAddModal()
      await refresh()
    } catch (error) {
      console.error('Bandsintown import failed:', error)
      alert('Bandsintown import failed. Please try again.')
    } finally {
      setBandsintownImporting(false)
    }
  }

  const handleTicketCountChange = (showId: string, value: string) => {
    setTicketCounts((prev) => ({ ...prev, [showId]: value }))
    setTicketDirty((prev) => new Set(prev).add(showId))
  }

  const handleTicketPercentageChange = (showId: string, value: string) => {
    setTicketPercentages((prev) => ({ ...prev, [showId]: value }))
    setTicketDirty((prev) => new Set(prev).add(showId))
  }

  const saveTicketForShow = async (show: Show) => {
    const showId = show.id
    if (!showId) return
    setTicketSaving((prev) => ({ ...prev, [showId]: true }))
    try {
      const raw = show.raw || {}
      const numericCount = Number(ticketCounts[showId] || 0) || 0
      const capacity = Number(show.sellable_cap || raw.capacity || 0) || 0
      const manualPercentage = ticketPercentages[showId]
      const autoPercentage =
        capacity > 0 && numericCount > 0 ? Math.round((numericCount / capacity) * 100) : 0
      const percentageToSave =
        manualPercentage !== undefined && manualPercentage !== '' ? Number(manualPercentage) || 0 : autoPercentage

      const updateData = {
        ...raw,
        current_ticket_count: numericCount,
        ticket_count: numericCount,
        tickets_sold: numericCount,
        tix_sold: numericCount,
        percentage_sold: percentageToSave,
        tix_sold_percent: percentageToSave,
        last_ticket_count_date: new Date().toISOString(),
      }

      await putDataAPI('user/updateBooking', {
        bookingId: showId,
        data: updateData,
      })

      setTicketDirty((prev) => {
        const next = new Set(prev)
        next.delete(showId)
        return next
      })
      await refresh()
    } catch (error) {
      console.error('Ticket update failed:', error)
      alert('Failed to update ticket count. Please try again.')
    } finally {
      setTicketSaving((prev) => ({ ...prev, [showId]: false }))
    }
  }

  const saveAllTickets = async (showsToSave: Show[]) => {
    for (const show of showsToSave) {
      if (!ticketDirty.has(show.id)) continue
      // eslint-disable-next-line no-await-in-loop
      await saveTicketForShow(show)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const required = ['perf_date', 'artist', 'city', 'state', 'venue'] as const
      const missing = required.filter((k) => !String(editData[k] ?? '').trim())
      if (missing.length) {
        alert(`Please fill required fields: ${missing.join(', ')}`)
        return
      }

      if (isEditing && editShowId) {
        // Update existing show
        await putDataAPI('user/updateBooking', {
          bookingId: editShowId,
          data: {
            ...editData,
            event_title: editData.event_title || editData.venue,
            perf_date: editData.perf_date,
          },
        })
      } else {
        // Create new show
        await postDataAPI('user/saveBookings', {
          data: {
            ...editData,
            event_title: editData.event_title || editData.venue,
            perf_date: editData.perf_date,
          },
        })
      }
      
      setIsAddOpen(false)
      resetAddModal()
      await refresh()
    } catch (error) {
      console.error('Failed to save show:', error)
      alert('Failed to save show. Please check fields and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const startEditShow = (show: Show) => {
    const raw = show.raw || {}
    setEditData({
      artist: show.artist || '',
      perf_date: show.perf_date || show.date || new Date().toISOString().split('T')[0],
      city: show.city || '',
      state: raw.state || '',
      venue: show.venue || '',
      event_title: show.event_title || show.title || '',
      status: show.status || 'pending',
      show_time: raw.show_time || '',
      announce_date: raw.announce_date || '',
      on_sale_date: raw.on_sale_date || '',
      guarantee: raw.guarantee || '',
      walkout_potential: raw.walkout_potential || '',
      sellable_cap: show.sellable_cap || '',
      promoter: raw.promoter || '',
      tix_sold_percent: show.tix_sold_percent || '',
      tix_sold: show.tix_sold || '',
      current_ticket_count: show.current_ticket_count || '',
      appearing_with: raw.appearing_with || '',
      red_flag: raw.red_flag || false,
      red_flag_notes: raw.red_flag_notes || '',
      promoter_company: raw.promoter_company || '',
      promoter_contact_names: raw.promoter_contact_names || '',
      promoter_contact_emails: raw.promoter_contact_emails || '',
      venue_category: raw.venue_category || '',
      billing_type: raw.billing_type || '',
      territory: raw.territory || '',
      advanced_info: raw.advanced_info || '',
      deal_type: raw.deal_type || '',
      deal_info: raw.deal_info || '',
      gross_potential: raw.gross_potential || '',
      net_potential: raw.net_potential || '',
      artist_net: raw.artist_net || '',
      artist_profit: raw.artist_profit || '',
      split_point: raw.split_point || '',
      amount_to_split: raw.amount_to_split || '',
      ticket_notes: raw.ticket_notes || '',
      ticket_prices_tiers: raw.ticket_prices_tiers || '',
      ticket_taxes_fees: raw.ticket_taxes_fees || '',
      total_fees: raw.total_fees || '',
      deposit_due: raw.deposit_due || '',
      deposit_received: raw.deposit_received || '',
      contract_status: raw.contract_status || '',
      currency_conversion: raw.currency_conversion || '',
      additional_clauses: raw.additional_clauses || '',
      schedule: raw.schedule || '',
      notes: raw.notes || '',
    })
    setIsEditing(true)
    setEditShowId(show.id)
    setAddView('manual')
    setIsAddOpen(true)
  }

  const handleDeleteShow = async (showId: string) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return
    try {
      await putDataAPI('user/deleteBooking', { bookingId: showId })
      await refresh()
      setOpenDropdown(null)
    } catch (error) {
      console.error('Failed to delete show:', error)
      alert('Failed to delete show. Please try again.')
    }
  }

  const toggleShowSelection = (showId: string) => {
    setSelectedShowIds((prev) => {
      const next = new Set(prev)
      if (next.has(showId)) next.delete(showId)
      else next.add(showId)
      return next
    })
  }

  const selectAllFiltered = () => {
    const searchLower = searchQuery.trim().toLowerCase()
    const filtered = shows.filter((show) => {
      if (statusFilter && (show.status || '') !== statusFilter) return false
      if (!searchLower) return true
      const title = (show.event_title || show.title || show.venue || '').toLowerCase()
      const venue = (show.venue || '').toLowerCase()
      const city = (show.city || '').toLowerCase()
      const artist = (show.artist || '').toLowerCase()
      return title.includes(searchLower) || venue.includes(searchLower) || city.includes(searchLower) || artist.includes(searchLower)
    })
    const ids = filtered.map((s) => s.id)
    if (ids.length === 0) return
    setSelectedShowIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        ids.forEach((id) => next.delete(id))
        return next
      }
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  const handleBulkDelete = async () => {
    if (selectedShowIds.size === 0) return
    if (!window.confirm(`Delete ${selectedShowIds.size} selected show${selectedShowIds.size === 1 ? '' : 's'}? This cannot be undone.`)) return
    setIsBulkDeleting(true)
    
    const idsToDelete = Array.from(selectedShowIds)
    let successCount = 0
    let errorCount = 0
    
    try {
      for (const showId of idsToDelete) {
        try {
          await putDataAPI('user/deleteBooking', { bookingId: showId })
          successCount++
        } catch (error) {
          console.error(`Failed to delete show ${showId}:`, error)
          errorCount++
        }
      }
      
      // Remove all successfully deleted shows from frontend
      setShows((prev) => prev.filter((s) => !selectedShowIds.has(s.id)))
      setSelectedShowIds(new Set())
      setOpenDropdown(null)
      
      // Show summary
      if (errorCount > 0) {
        alert(`Deleted ${successCount} show(s). ${errorCount} failed.`)
      }
      
      await refresh()
    } catch (error) {
      console.error('Bulk delete failed:', error)
      alert('Failed to delete shows. Please try again.')
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const totalTickets = shows.reduce((sum, show) => sum + getTicketCount(show), 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const pastShows = shows
    .filter((show) => {
      const showDate = new Date(show.perf_date || show.date || '')
      if (Number.isNaN(showDate.getTime())) return false
      showDate.setHours(0, 0, 0, 0)
      return showDate < today
    })
    .sort(
      (a, b) =>
        new Date(b.perf_date || b.date || '').getTime() -
        new Date(a.perf_date || a.date || '').getTime()
    )
  const lastShow = pastShows[0]
  const lastShowTickets = lastShow ? getTicketCount(lastShow) : 0

  const upcomingShows = shows
    .filter((show) => {
      const showDate = new Date(show.perf_date || show.date || '')
      if (Number.isNaN(showDate.getTime())) return false
      showDate.setHours(0, 0, 0, 0)
      return showDate >= today
    })
    .sort(
      (a, b) =>
        new Date(a.perf_date || a.date || '').getTime() -
        new Date(b.perf_date || b.date || '').getTime()
    )
  const nextShow = upcomingShows[0]
  const nextShowDate = nextShow?.perf_date ?? nextShow?.date

  // Apply search and status filter
  const searchLower = searchQuery.trim().toLowerCase()
  const filteredShows = shows.filter((show) => {
    if (statusFilter && (show.status || '') !== statusFilter) return false
    if (!searchLower) return true
    const title = (show.event_title || show.title || show.venue || '').toLowerCase()
    const venue = (show.venue || '').toLowerCase()
    const city = (show.city || '').toLowerCase()
    const artist = (show.artist || '').toLowerCase()
    return title.includes(searchLower) || venue.includes(searchLower) || city.includes(searchLower) || artist.includes(searchLower)
  })

  const modalMeta = {
    options: {
      title: 'Add Show',
      description: 'Choose how you want to add a show.',
    },
    manual: {
      title: 'Manual Add',
      description: 'Fill in required fields.',
    },
    csv: {
      title: 'CSV / Excel Import',
      description: 'Upload a CSV export to import shows.',
    },
    bandsintown: {
      title: 'Bandsintown Import',
      description: 'Fetch events from Bandsintown.',
    },
    'ticket-counter': {
      title: 'Update Tickets',
      description: 'Update ticket counts for upcoming shows.',
    },
  } as const

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Shows</h1>
          <p className="text-sm text-gray-500">Canonical record of all live events</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setAddView('ticket-counter')
              setIsAddOpen(true)
            }}
          >
            Update Tickets
          </Button>
          <Button
            onClick={() => {
              setAddView('options')
              setIsAddOpen(true)
            }}
          >
            Add Show
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false)
          resetAddModal()
        }}
        title={isEditing && addView === 'manual' ? 'Edit Show' : modalMeta[addView].title}
        description={isEditing && addView === 'manual' ? 'Update show details.' : modalMeta[addView].description}
        maxWidthClassName="max-w-4xl"
      >
        {addView === 'options' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                key: 'ticket-counter',
                title: 'Ticket Counter',
                description: 'Update ticket counts for upcoming shows.',
              },
              { key: 'manual', title: 'Manual Add', description: 'Add a show with full details.' },
              { key: 'csv', title: 'CSV/Excel', description: 'Import shows from a CSV export.' },
              { key: 'bandsintown', title: 'Bandsintown', description: 'Pull in events from Bandsintown.' },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className="text-left rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:bg-gray-50/50 transition-colors"
                onClick={() => setAddView(option.key as any)}
              >
                <div className="text-sm font-medium text-gray-900">{option.title}</div>
                <div className="text-xs text-gray-500 mt-1">{option.description}</div>
              </button>
            ))}
          </div>
        ) : addView === 'manual' ? (
          <form onSubmit={handleCreate} className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Required details</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
              {basicFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-2">
                    {field.label}
                    {field.required ? <span className="text-red-500 ml-1">*</span> : null}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-4 transition-colors"
              >
                {showAdvanced ? '− Hide advanced fields' : '+ Show advanced fields'}
              </button>
            </div>

            {showAdvanced ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
                {advancedFields.map((field) => (
                  <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-sm font-medium mb-2">{field.label}</label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            ) : null}

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? (isEditing ? 'Updating…' : 'Creating…') : (isEditing ? 'Update Show' : 'Create Show')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setIsAddOpen(false)
                  resetAddModal()
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : addView === 'csv' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Upload CSV</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            <div className="relative">
              <input
                type="file"
                id="csv-file-input"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    void handleCsvFileChange(file)
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <label
                htmlFor="csv-file-input"
                className="flex items-center justify-center gap-3 w-full px-6 py-8 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/30 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 cursor-pointer group"
              >
                <svg
                  className="w-8 h-8 text-gray-400 group-hover:text-gray-600 transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                    {csvFileName || 'Choose a file or drag it here'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">CSV, XLSX, or XLS files</div>
                </div>
              </label>
            </div>
            {csvFileName ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-50 border border-gray-200">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="text-xs text-gray-600 flex-1 truncate">{csvFileName}</div>
              </div>
            ) : null}
            {csvLoading ? <div className="text-sm text-gray-500">Parsing file…</div> : null}
            {csvError ? <div className="text-sm text-red-500">{csvError}</div> : null}
            {csvRows.length > 0 ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-500">{csvRows.length} rows ready to import</div>
                <div className="max-h-[240px] overflow-y-auto space-y-2">
                  {csvRows.slice(0, 8).map((row, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-3">
                      <div className="text-sm font-medium text-gray-900">
                        {row.event_title || row.venue || 'Untitled show'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {row.perf_date || 'No date'} • {row.city || 'Unknown city'}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" onClick={handleCsvImport} disabled={csvImporting}>
                  {csvImporting ? 'Importing…' : 'Import Shows'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : addView === 'bandsintown' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Bandsintown settings</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Artist name</label>
                <input
                  type="text"
                  value={bandsintownArtist}
                  onChange={(e) => setBandsintownArtist(e.target.value)}
                  placeholder="Artist name"
                  className="ds-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bandsintown App ID</label>
                <input
                  type="text"
                  value={bandsintownAppId}
                  onChange={(e) => setBandsintownAppId(e.target.value)}
                  placeholder="App ID"
                  className="ds-input"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={handleBandsintownPreview}
              disabled={bandsintownLoading || !bandsintownArtist.trim() || !bandsintownAppId.trim()}
            >
              {bandsintownLoading ? 'Fetching…' : 'Preview Events'}
            </Button>
            {bandsintownError ? <div className="text-sm text-red-500">{bandsintownError}</div> : null}
            {bandsintownEvents.length > 0 ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-500">
                  {bandsintownEvents.length} events ready to import
                </div>
                <div className="max-h-[240px] overflow-y-auto space-y-2">
                  {bandsintownEvents.slice(0, 8).map((event, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-3">
                      <div className="text-sm font-medium text-gray-900">
                        {event.event_title || event.venue || 'Untitled show'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {event.perf_date || 'No date'} • {event.city || 'Unknown city'}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" onClick={handleBandsintownImport} disabled={bandsintownImporting}>
                  {bandsintownImporting ? 'Importing…' : 'Import Events'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Update Tickets</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            {upcomingShows.length === 0 ? (
              <div className="text-sm text-gray-500">No upcoming shows to update.</div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {upcomingShows.length} upcoming shows
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => saveAllTickets(upcomingShows)}
                    disabled={ticketDirty.size === 0}
                  >
                    Save All
                  </Button>
                </div>
                <div className="max-h-[360px] overflow-y-auto space-y-3">
                  {upcomingShows.map((show) => {
                    const showId = show.id
                    const countValue = ticketCounts[showId] ?? ''
                    const percentValue = ticketPercentages[showId] ?? ''
                    const isSaving = !!ticketSaving[showId]
                    return (
                      <div key={showId} className="rounded-lg border border-gray-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {show.event_title || show.venue || 'Untitled show'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {show.city || 'Unknown city'} •{' '}
                              {show.perf_date ? new Date(show.perf_date).toLocaleDateString() : 'No date'}
                            </div>
                          </div>
                          <Button type="button" onClick={() => saveTicketForShow(show)} disabled={isSaving}>
                            {isSaving ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Ticket Count
                            </label>
                            <input
                              type="number"
                              value={countValue}
                              onChange={(e) => handleTicketCountChange(showId, e.target.value)}
                              className="ds-input"
                              placeholder="Enter tickets sold"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              % Sold (optional)
                            </label>
                            <input
                              type="number"
                              value={percentValue}
                              onChange={(e) => handleTicketPercentageChange(showId, e.target.value)}
                              className="ds-input"
                              placeholder="Auto-calculates if blank"
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Summary Stats */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Total Tickets</div>
          <div className="text-2xl font-semibold text-gray-900">{totalTickets.toLocaleString()}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Last Show Sold</div>
          <div className="text-2xl font-semibold text-gray-900">{lastShowTickets.toLocaleString()}</div>
          {lastShow && (
            <div className="text-xs text-gray-500 mt-1 truncate">
              {lastShow.event_title || lastShow.venue} • {lastShow.city}
            </div>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Next Up</div>
          {nextShow ? (
            <>
              <div className="text-sm font-medium text-gray-900 truncate">
                {nextShow.event_title || nextShow.venue}
              </div>
              <div className="text-xs text-gray-500 mt-1 truncate">
                {nextShow.city} • {nextShowDate ? new Date(nextShowDate).toLocaleDateString() : 'TBD'}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500">No upcoming shows</div>
          )}
        </div>
      </div>

      {/* Search & Filters (above table) */}
      {shows.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, venue, city, or artist..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="announced">Announced</option>
            <option value="private">Private</option>
            <option value="canceled">Canceled</option>
          </select>
          {(searchQuery.trim() || statusFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                setStatusFilter('')
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear filters
            </button>
          )}
          <span className="text-sm text-gray-500">
            {filteredShows.length} of {shows.length} show{shows.length === 1 ? '' : 's'}
          </span>
          {filteredShows.length > 0 ? (
            <div className="flex items-center gap-3 ml-auto">
              {selectedShowIds.size === 0 ? (
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                >
                  Select all
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedShowIds(new Set())}
                    className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                  >
                    Clear
                  </button>
                  <Button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm py-1.5 px-3"
                  >
                    {isBulkDeleting ? 'Deleting…' : `Delete ${selectedShowIds.size} selected`}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Shows Table */}
      {shows.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Venue</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Tickets</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Venue Capacity</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">% Sold</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredShows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-sm text-gray-500">
                    No shows match your search or filters. Try adjusting or{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('')
                        setStatusFilter('')
                      }}
                      className="text-gray-900 font-medium hover:underline"
                    >
                      clear filters
                    </button>
                    .
                  </td>
                </tr>
              ) : (
              filteredShows.map((show) => {
                const venueCapacity = show.sellable_cap || null
                const percentSoldRaw = show.tix_sold_percent ?? null
                const percentSold = percentSoldRaw !== null ? Number(percentSoldRaw) : null
                const key = (show as any)._id || show.id || `${show.title}-${show.perf_date || show.date}`
                const d = show.date || show.perf_date
                const isSelected = selectedShowIds.has(show.id)
                return (
                  <tr
                    key={key}
                    className={`transition-colors hover:bg-gray-50/50 dark:hover:bg-white/[0.04] cursor-pointer ${isSelected ? 'bg-gray-100 dark:bg-white/[0.08]' : ''}`}
                    onClick={() => toggleShowSelection(show.id)}
                  >
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {d ? new Date(d).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {show.venue}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {show.city}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-900 font-medium">
                      {getTicketCount(show).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">
                      {venueCapacity ? venueCapacity.toLocaleString() : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-900 font-medium">
                      {percentSold !== null && !Number.isNaN(percentSold) ? `${percentSold.toFixed(1)}%` : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        show.status === 'confirmed' ? 'bg-green-50 text-green-700' :
                        show.status === 'announced' ? 'bg-blue-50 text-blue-700' :
                        show.status === 'canceled' ? 'bg-red-50 text-red-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {show.status || 'unknown'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenDropdown(openDropdown === show.id ? null : show.id)
                          }}
                          className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 rounded p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        {openDropdown === show.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20 py-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  startEditShow(show)
                                  setOpenDropdown(null)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteShow(show.id)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-sm text-gray-500">No shows found</p>
        </div>
      )}
    </div>
  )
}
